import {
  mat3PureTranslation,
  mat3RectToQuad,
  mat3Translation,
  type Mat3,
} from '../core/math';
import {
  TRANSFORM_ANCHORS,
  type Point,
  type Rect,
  type TransformAnchor,
  type TransformMode,
} from '../core/types';
import {
  identityMesh,
  isIdentityMesh,
  presetMesh,
  warpPoint,
  WARP_ORDER,
  type WarpPreset,
} from '../core/warp';
import type { FloatingSelection } from '../engine/floatOps';
import { docMatrixToSrcUv } from '../gl/view';
import {
  commitSelectionTransform,
  commitTransform,
  commitWarp,
  deselect,
  dropFloatingSelection,
  layerContentBounds,
  liftFloatingSelection,
  nudgeLayerOffset,
  previewWarp,
  selectionBounds,
} from '../state/actions';
import { liveRender, setLivePreview } from '../state/renderState';
import {
  editTextLayer,
  moveTextLayer,
  topTextLayerAt,
} from '../state/textActions';
import { transformShapeLayer, warpShapeLayer } from '../state/vectorActions';
import { useAppStore, type TransformTarget } from '../state/store';
import {
  movableLayer,
  type OverlayContext,
  type Tool,
  type ToolContext,
  type ToolPointerEvent,
} from './types';

const HANDLE_HIT_RADIUS = 9;
const HANDLE_SIZE = 8;

/** Corner order everywhere in this file: top left, top right, bottom right, bottom left. */
type Corners = [Point, Point, Point, Point];

type HandleId =
  | { kind: 'corner'; index: number }
  | { kind: 'edge'; index: number }
  | { kind: 'mesh'; index: number }
  | { kind: 'move' }
  | { kind: 'rotate' };

interface Session {
  /** What the matrix will be baked into when the session is applied. */
  target: TransformTarget;
  layerId: string | null;
  /** Box the layer content is mapped from; never changes during the session. */
  original: Rect;
  /** Where those four corners are now; a general quad, so perspective is possible. */
  corners: Corners;
  /** Rotation and symmetric-scale centre, fixed for the whole session. */
  pivot: Point;
  /** Warp control net, in document pixels, or null outside Warp mode. */
  mesh: Point[] | null;
  preset: WarpPreset;
  /** -1..1 bend of the chosen preset. */
  bend: number;
}

interface DragState {
  handle: HandleId;
  startCorners: Corners;
  startMesh: Point[] | null;
  startPointer: Point;
  startAngle: number;
}

function rectCorners(rect: Rect): Corners {
  const { x, y, width, height } = rect;
  return [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height },
  ];
}

function cloneCorners(corners: Corners): Corners {
  return [{ ...corners[0] }, { ...corners[1] }, { ...corners[2] }, { ...corners[3] }];
}

/** Edge `i` runs from corner `i` to corner `i + 1`, so edge 0 is the top one. */
function edgeMid(corners: Corners, index: number): Point {
  const a = corners[index];
  const b = corners[(index + 1) % 4];
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function quadCentre(corners: Corners): Point {
  return {
    x: (corners[0].x + corners[1].x + corners[2].x + corners[3].x) / 4,
    y: (corners[0].y + corners[1].y + corners[2].y + corners[3].y) / 4,
  };
}

function subtract(a: Point, b: Point): Point {
  return { x: a.x - b.x, y: a.y - b.y };
}

function add(a: Point, b: Point): Point {
  return { x: a.x + b.x, y: a.y + b.y };
}

function scalePoint(p: Point, k: number): Point {
  return { x: p.x * k, y: p.y * k };
}

function dot(a: Point, b: Point): number {
  return a.x * b.x + a.y * b.y;
}

/** Solves `p = a·e1 + b·e2`, the coordinates of `p` in the quad's own frame. */
function solveBasis(p: Point, e1: Point, e2: Point): { a: number; b: number } {
  const det = e1.x * e2.y - e2.x * e1.y;
  if (Math.abs(det) < 1e-9) return { a: 1, b: 1 };
  return {
    a: (p.x * e2.y - e2.x * p.y) / det,
    b: (e1.x * p.y - p.x * e1.y) / det,
  };
}

function rotateAround(point: Point, pivot: Point, angle: number): Point {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const dx = point.x - pivot.x;
  const dy = point.y - pivot.y;
  return { x: pivot.x + dx * cos - dy * sin, y: pivot.y + dx * sin + dy * cos };
}

function sessionMatrix(session: Session): Mat3 {
  return mat3RectToQuad(session.original, session.corners);
}

/** Corner drag with the opposite corner pinned: the plain Free Transform scale. */
function scaleFromOpposite(
  start: Corners,
  index: number,
  pointer: Point,
  keepAspect: boolean,
): Corners {
  const anchor = start[(index + 2) % 4];
  const along = start[(index + 1) % 4];
  const across = start[(index + 3) % 4];
  const e1 = subtract(along, anchor);
  const e2 = subtract(across, anchor);
  let { a, b } = solveBasis(subtract(pointer, anchor), e1, e2);
  if (keepAspect) {
    const uniform = (a + b) / 2;
    a = uniform;
    b = uniform;
  }

  const next = cloneCorners(start);
  next[index] = add(anchor, add(scalePoint(e1, a), scalePoint(e2, b)));
  next[(index + 1) % 4] = add(anchor, scalePoint(e1, a));
  next[(index + 3) % 4] = add(anchor, scalePoint(e2, b));
  return next;
}

/** Corner drag with Alt: the box grows around the reference point instead. */
function scaleAboutPivot(
  start: Corners,
  index: number,
  pointer: Point,
  keepAspect: boolean,
  pivot: Point,
): Corners {
  const centre = quadCentre(start);
  const right = subtract(edgeMid(start, 1), centre);
  const down = subtract(edgeMid(start, 2), centre);
  const from = solveBasis(subtract(start[index], pivot), right, down);
  const to = solveBasis(subtract(pointer, pivot), right, down);
  // A corner sitting on the reference point cannot say how far it has moved.
  if (Math.abs(from.a) < 1e-6 || Math.abs(from.b) < 1e-6) {
    return scaleFromOpposite(start, index, pointer, keepAspect);
  }

  let sx = to.a / from.a;
  let sy = to.b / from.b;
  if (keepAspect) {
    const uniform = (sx + sy) / 2;
    sx = uniform;
    sy = uniform;
  }

  return start.map((corner) => {
    const local = solveBasis(subtract(corner, pivot), right, down);
    return add(pivot, add(scalePoint(right, local.a * sx), scalePoint(down, local.b * sy)));
  }) as Corners;
}

/** Edge drag: one side follows the pointer, the opposite side stays where it is. */
function scaleEdge(start: Corners, index: number, pointer: Point, symmetric: boolean): Corners {
  const opposite = (index + 2) % 4;
  const anchorMid = edgeMid(start, opposite);
  const centre = quadCentre(start);
  const base = symmetric ? centre : anchorMid;
  const direction = subtract(edgeMid(start, index), base);
  const length = dot(direction, direction);
  if (length < 1e-9) return cloneCorners(start);
  const factor = dot(subtract(pointer, base), direction) / length;

  // Corner `index` pairs with corner `opposite + 1`, and `index + 1` with `opposite`.
  const pairs: [number, number][] = [
    [index, (opposite + 1) % 4],
    [(index + 1) % 4, opposite],
  ];
  const next = cloneCorners(start);
  for (const [moving, pinned] of pairs) {
    const anchor = symmetric ? centre : start[pinned];
    next[moving] = add(anchor, scalePoint(subtract(start[moving], anchor), factor));
    if (symmetric) {
      next[pinned] = add(anchor, scalePoint(subtract(start[pinned], anchor), factor));
    }
  }
  return next;
}

/** Skew: the dragged edge slides along its own direction, keeping its length. */
function skewEdge(start: Corners, index: number, pointer: Point, startPointer: Point): Corners {
  const a = start[index];
  const b = start[(index + 1) % 4];
  const tangent = subtract(b, a);
  const length = dot(tangent, tangent);
  if (length < 1e-9) return cloneCorners(start);
  const shift = scalePoint(tangent, dot(subtract(pointer, startPointer), tangent) / length);

  const next = cloneCorners(start);
  next[index] = add(a, shift);
  next[(index + 1) % 4] = add(b, shift);
  return next;
}

/** Perspective: the dragged corner's edge partner mirrors it, so the edge tapers. */
function perspectiveCorner(start: Corners, index: number, pointer: Point): Corners {
  // Corners 0 and 1 share the top edge, 2 and 3 the bottom one.
  const partner = index === 0 ? 1 : index === 1 ? 0 : index === 2 ? 3 : 2;
  const edge = index < 2 ? subtract(start[1], start[0]) : subtract(start[2], start[3]);
  const length = Math.hypot(edge.x, edge.y);
  if (length < 1e-9) return cloneCorners(start);
  const tangent = scalePoint(edge, 1 / length);
  const normal = { x: -tangent.y, y: tangent.x };

  const delta = subtract(pointer, start[index]);
  const alongEdge = dot(delta, tangent);
  const acrossEdge = dot(delta, normal);

  const next = cloneCorners(start);
  next[index] = add(start[index], delta);
  next[partner] = add(
    start[partner],
    add(scalePoint(tangent, -alongEdge), scalePoint(normal, acrossEdge)),
  );
  return next;
}

function containsPoint(corners: Corners, point: Point): boolean {
  let sign = 0;
  for (let i = 0; i < 4; i += 1) {
    const edge = subtract(corners[(i + 1) % 4], corners[i]);
    const toPoint = subtract(point, corners[i]);
    const cross = edge.x * toPoint.y - edge.y * toPoint.x;
    if (Math.abs(cross) < 1e-9) continue;
    const current = cross > 0 ? 1 : -1;
    if (sign === 0) sign = current;
    else if (sign !== current) return false;
  }
  return true;
}

/** Steps per patch edge when drawing the warp grid; smooth enough on screen. */
const WARP_OVERLAY_STEPS = 12;

/**
 * The warp grid: the patch boundary, the two lines each way that divide it into
 * nine cells, and a handle on each of the sixteen control points.
 */
function drawWarpOverlay(overlay: OverlayContext, mesh: Point[]): void {
  const { ctx: c } = overlay;
  c.save();
  c.strokeStyle = 'rgba(90,170,255,0.95)';
  c.lineWidth = 1;

  const trace = (at: (i: number) => Point): void => {
    c.beginPath();
    for (let i = 0; i <= WARP_OVERLAY_STEPS; i += 1) {
      const point = overlay.toScreen(at(i / WARP_OVERLAY_STEPS));
      if (i === 0) c.moveTo(point.x, point.y);
      else c.lineTo(point.x, point.y);
    }
    c.stroke();
  };

  for (const t of [0, 1 / 3, 2 / 3, 1]) {
    trace((s) => warpPoint(mesh, s, t));
    trace((s) => warpPoint(mesh, t, s));
  }

  for (let i = 0; i < mesh.length; i += 1) {
    const point = overlay.toScreen(mesh[i]);
    // The four patch corners are the ones that move the shape itself.
    const isCorner = i === 0 || i === 3 || i === 12 || i === 15;
    const size = isCorner ? HANDLE_SIZE : HANDLE_SIZE - 3;
    c.fillStyle = isCorner ? '#fff' : 'rgba(90,170,255,0.95)';
    c.fillRect(point.x - size / 2, point.y - size / 2, size, size);
    if (isCorner) c.strokeRect(point.x - size / 2, point.y - size / 2, size, size);
  }
  c.restore();
}

/**
 * Session state lives at module level so the Transform submenu and the options
 * bar can read and rewrite the box while the tool is running.
 */
let session: Session | null = null;
let drag: DragState | null = null;
/** Last context the tool saw, so menu commands can drive an active session. */
let context: ToolContext | null = null;
/** Filled in by the tool below; both need its private helpers. */
let applySession: (ctx: ToolContext) => void = () => {};
let finishSession: (ctx: ToolContext) => void = () => {};
let publishSession: (ctx: ToolContext) => void = () => {};

/**
 * The options bar mirrors the box in numbers, so it has to hear about every
 * change. A private subscription keeps that out of the store, where a bump on
 * every pointer move would re-render far more than the one row that cares.
 */
const listeners = new Set<() => void>();
let revision = 0;

export function subscribeTransform(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function transformRevision(): number {
  return revision;
}

function announce(): void {
  revision += 1;
  for (const listener of listeners) listener();
}

/**
 * Free transform. The preview costs nothing extra: the compositor already knows
 * how to sample a layer through an inverse matrix, so the layer's own pixels stay
 * untouched until the transform is committed.
 */
export const transformTool: Tool = (() => {
  const publish = (ctx: ToolContext): void => {
    context = ctx;
    announce();
    if (!session) {
      liveRender.transform = null;
      liveRender.selectionTransform = null;
      return;
    }
    // A warp cannot be written as a matrix, so its preview is a warped copy of
    // the layer standing in for the layer's own pixels.
    if (session.mesh && session.layerId) {
      previewWarp(session.layerId, session.original, session.mesh);
      ctx.requestRender();
      return;
    }
    const srcMatrix = docMatrixToSrcUv(sessionMatrix(session), ctx.doc.width, ctx.doc.height);
    if (session.target === 'selection') {
      liveRender.selectionTransform = srcMatrix;
    } else if (session.layerId) {
      liveRender.transform = { layerId: session.layerId, srcMatrix };
    }
    ctx.requestRender();
  };

  const start = (ctx: ToolContext): void => {
    context = ctx;
    const target = useAppStore.getState().transformTarget;
    const layerId = target === 'layer' ? movableLayer(ctx.doc) : null;
    if (target === 'layer' && !layerId) {
      ctx.setStatus('The active layer is locked or not selected');
      return;
    }
    if (target === 'selection' && !ctx.doc.hasSelection) {
      ctx.setStatus('Transform Selection needs a selection');
      return;
    }

    // The box hugs the layer's own pixels, as Photoshop's does; a selection wins
    // over that, and an empty layer falls back to the whole canvas.
    const canvas = { x: 0, y: 0, width: ctx.doc.width, height: ctx.doc.height };
    const bounds =
      selectionBounds() ?? (layerId ? layerContentBounds(layerId) : null) ?? canvas;
    // Warping a selection outline has no meaning, so it keeps the matrix path
    // even when Warp is picked. A shape layer can warp: its geometry bends.
    const warping = ctx.options.transformMode === 'warp' && target === 'layer' && layerId != null;
    session = {
      target,
      layerId,
      original: { ...bounds },
      corners: rectCorners(bounds),
      pivot: { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 },
      mesh: warping ? identityMesh(bounds) : null,
      preset: 'custom',
      bend: 0,
    };
    syncTransformPivot(ctx.options.transformAnchor);
    publish(ctx);
    ctx.setStatus(
      warping
        ? 'Drag the warp handles or pick a preset. Enter applies, Esc cancels'
        : target === 'selection'
          ? 'Transforming the selection. Enter applies, Esc cancels'
          : 'Drag the handles. Enter applies, Esc cancels',
    );
  };

  const reset = (ctx: ToolContext): void => {
    if (session?.mesh && session.layerId) setLivePreview('surface', session.layerId, null);
    session = null;
    drag = null;
    liveRender.transform = null;
    liveRender.selectionTransform = null;
    announce();
    ctx.requestRender();
  };

  const commit = (ctx: ToolContext): boolean => {
    if (!session) return false;
    const { target, layerId, mesh, original } = session;
    const docMatrix = sessionMatrix(session);
    const srcMatrix = docMatrixToSrcUv(docMatrix, ctx.doc.width, ctx.doc.height);
    const meshChanged = mesh != null && !isIdentityMesh(mesh, original);
    const translation = !mesh && target === 'layer' ? mat3PureTranslation(docMatrix) : null;
    reset(ctx);

    if (mesh && layerId) {
      if (meshChanged && !warpShapeLayer(layerId, original, mesh)) {
        commitWarp(layerId, original, mesh);
      }
    } else if (target === 'selection') {
      commitSelectionTransform(srcMatrix);
      // A shape is warped as geometry, so it stays crisp instead of being resampled.
    } else if (layerId && translation) {
      // Pure moves keep pixels past the canvas via contentOffset — never bake.
      if (!transformShapeLayer(layerId, docMatrix, 'Free Transform')) {
        if (!moveTextLayer(layerId, translation.x, translation.y, 'Free Transform')) {
          nudgeLayerOffset(layerId, translation.x, translation.y, 'Free Transform');
        }
      }
    } else if (layerId && !transformShapeLayer(layerId, docMatrix)) {
      commitTransform(layerId, srcMatrix);
    }
    return true;
  };

  /** Bake and keep transforming (Flip / Rotate while a session is open). */
  const apply = (ctx: ToolContext): void => {
    if (!commit(ctx)) return;
    start(ctx);
  };

  /** Bake and leave Free Transform, as Photoshop's Enter does. */
  const finish = (ctx: ToolContext): void => {
    // Transform Selection keeps the outline; Free Transform drops it so the
    // marching ants do not linger after Enter.
    const clearSelection = session?.target === 'layer';
    if (!commit(ctx)) return;
    if (clearSelection) deselect();
    useAppStore.getState().setTool('move');
    ctx.setStatus('Transform applied');
  };

  const hitTest = (event: ToolPointerEvent, ctx: ToolContext): HandleId => {
    if (!session) return { kind: 'move' };
    const { corners, mesh } = session;
    const distance = (point: Point): number =>
      Math.hypot(point.x - event.rawPoint.x, point.y - event.rawPoint.y) * ctx.view.zoom;

    if (mesh) {
      let best = -1;
      let bestDistance = HANDLE_HIT_RADIUS;
      for (let i = 0; i < mesh.length; i += 1) {
        const away = distance(mesh[i]);
        if (away <= bestDistance) {
          best = i;
          bestDistance = away;
        }
      }
      return best >= 0 ? { kind: 'mesh', index: best } : { kind: 'move' };
    }

    for (let i = 0; i < 4; i += 1) {
      if (distance(corners[i]) <= HANDLE_HIT_RADIUS) return { kind: 'corner', index: i };
    }
    for (let i = 0; i < 4; i += 1) {
      if (distance(edgeMid(corners, i)) <= HANDLE_HIT_RADIUS) return { kind: 'edge', index: i };
    }
    return containsPoint(corners, event.rawPoint) ? { kind: 'move' } : { kind: 'rotate' };
  };

  applySession = apply;
  finishSession = finish;
  publishSession = publish;

  return {
    id: 'transform',
    cursor: 'move',
    snaps: true,
    hint: 'Drag handles, Ctrl distorts, Alt works from the reference point, Enter applies',

    activate(ctx) {
      start(ctx);
    },

    onPointerDown(event, ctx) {
      if (!session) start(ctx);
      if (!session) return;
      syncTransformPivot(ctx.options.transformAnchor);
      drag = {
        handle: hitTest(event, ctx),
        startCorners: cloneCorners(session.corners),
        startMesh: session.mesh ? session.mesh.map((point) => ({ ...point })) : null,
        startPointer: event.point,
        startAngle: Math.atan2(event.point.y - session.pivot.y, event.point.x - session.pivot.x),
      };
    },

    onPointerMove(event, ctx) {
      if (!drag || !session) return;
      const { startCorners, startPointer, startMesh } = drag;

      if (startMesh && session.mesh) {
        const delta = subtract(event.point, startPointer);
        if (drag.handle.kind === 'mesh') {
          const moved = session.mesh.map((point) => ({ ...point }));
          moved[drag.handle.index] = add(startMesh[drag.handle.index], delta);
          session.mesh = moved;
          // Dragging by hand means the shape no longer follows any preset.
          session.preset = 'custom';
        } else {
          session.mesh = startMesh.map((point) => add(point, delta));
        }
        publish(ctx);
        return;
      }

      if (drag.handle.kind === 'rotate') {
        const angle = Math.atan2(event.point.y - session.pivot.y, event.point.x - session.pivot.x);
        let turn = angle - drag.startAngle;
        if (event.shift) turn = Math.round(turn / (Math.PI / 12)) * (Math.PI / 12);
        session.corners = startCorners.map((corner) =>
          rotateAround(corner, session!.pivot, turn),
        ) as Corners;
        publish(ctx);
        return;
      }

      if (drag.handle.kind === 'move') {
        const delta = subtract(event.point, startPointer);
        session.corners = startCorners.map((corner) => add(corner, delta)) as Corners;
        publish(ctx);
        return;
      }

      // Ctrl reaches the free-corner modes without leaving Free Transform, the
      // way Photoshop does; the mode picker in the options bar is the other way.
      const mode = event.ctrl
        ? drag.handle.kind === 'corner'
          ? 'distort'
          : 'skew'
        : ctx.options.transformMode;
      const keepAspect = ctx.options.transformKeepAspect !== event.shift;
      const index = drag.handle.index;

      if (drag.handle.kind === 'corner') {
        if (mode === 'distort') {
          const next = cloneCorners(startCorners);
          next[index] = event.point;
          session.corners = next;
        } else if (mode === 'perspective') {
          session.corners = perspectiveCorner(startCorners, index, event.point);
        } else if (mode === 'skew') {
          session.corners = skewEdge(startCorners, index, event.point, startPointer);
        } else if (event.alt) {
          session.corners = scaleAboutPivot(
            startCorners,
            index,
            event.point,
            keepAspect,
            session.pivot,
          );
        } else {
          session.corners = scaleFromOpposite(startCorners, index, event.point, keepAspect);
        }
      } else if (mode === 'skew' || mode === 'distort' || mode === 'perspective') {
        session.corners = skewEdge(startCorners, index, event.point, startPointer);
      } else {
        session.corners = scaleEdge(startCorners, index, event.point, event.alt);
      }
      publish(ctx);
    },

    onPointerUp() {
      drag = null;
    },

    onDoubleClick(_event, ctx) {
      finish(ctx);
    },

    onKeyDown(event, ctx) {
      if (event.key === 'Enter' || event.code === 'NumpadEnter') {
        finish(ctx);
        return true;
      }
      if (event.key === 'Escape') {
        reset(ctx);
        useAppStore.getState().setTool('move');
        ctx.setStatus('Transform cancelled');
        return true;
      }
      return false;
    },

    cancel(ctx) {
      reset(ctx);
    },

    drawOverlay(overlay) {
      if (!session) return;
      const { corners, pivot, mesh } = session;
      const { ctx: c } = overlay;

      if (mesh) {
        drawWarpOverlay(overlay, mesh);
        return;
      }

      const outline = corners.map(overlay.toScreen);

      c.save();
      c.strokeStyle = 'rgba(90,170,255,0.95)';
      c.lineWidth = 1;
      c.beginPath();
      c.moveTo(outline[0].x, outline[0].y);
      for (let i = 1; i < outline.length; i += 1) c.lineTo(outline[i].x, outline[i].y);
      c.closePath();
      c.stroke();

      c.fillStyle = '#fff';
      const handles = [...corners, ...[0, 1, 2, 3].map((i) => edgeMid(corners, i))];
      for (const point of handles) {
        const p = overlay.toScreen(point);
        c.fillRect(p.x - HANDLE_SIZE / 2, p.y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
        c.strokeRect(p.x - HANDLE_SIZE / 2, p.y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
      }

      // The pivot, so it is clear what rotation and Alt-scaling turn around.
      const centre = overlay.toScreen(pivot);
      c.beginPath();
      c.arc(centre.x, centre.y, 4, 0, Math.PI * 2);
      c.stroke();
      c.beginPath();
      c.moveTo(centre.x - 6, centre.y);
      c.lineTo(centre.x + 6, centre.y);
      c.moveTo(centre.x, centre.y - 6);
      c.lineTo(centre.x, centre.y + 6);
      c.stroke();
      c.restore();
    },
  };
})();

/**
 * Entry points for the two Free Transform flavours. The target lives in the
 * store so that switching it re-activates the tool and starts a fresh session.
 */
export function startFreeTransform(): void {
  const store = useAppStore.getState();
  if (!store.activeDocumentId) {
    store.setStatus('Open a document to transform');
    return;
  }
  store.setTransformTarget('layer');
  store.setTool('transform');
}

export function startTransformSelection(): void {
  const store = useAppStore.getState();
  store.setTransformTarget('selection');
  store.setTool('transform');
}

export type QuickTransform = 'flipH' | 'flipV' | 'rotate90cw' | 'rotate90ccw' | 'rotate180';

/** Each fixed Transform submenu entry is one permutation of the four corners. */
function permuteCorners(corners: Corners, kind: QuickTransform): Corners {
  const [nw, ne, se, sw] = corners;
  switch (kind) {
    case 'flipH':
      return [ne, nw, sw, se];
    case 'flipV':
      return [sw, se, ne, nw];
    case 'rotate90cw':
      return [sw, nw, ne, se];
    case 'rotate90ccw':
      return [ne, se, sw, nw];
    case 'rotate180':
      return [se, sw, nw, ne];
  }
}

/** The same permutation over the warp net, so a flip in Warp mode flips the bend. */
function permuteMesh(mesh: Point[], kind: QuickTransform): Point[] {
  const at = (row: number, column: number): Point => mesh[row * WARP_ORDER + column];
  const last = WARP_ORDER - 1;
  const source = (row: number, column: number): Point => {
    switch (kind) {
      case 'flipH':
        return at(row, last - column);
      case 'flipV':
        return at(last - row, column);
      case 'rotate90cw':
        return at(last - column, row);
      case 'rotate90ccw':
        return at(column, last - row);
      case 'rotate180':
        return at(last - row, last - column);
    }
  };

  const next: Point[] = [];
  for (let row = 0; row < WARP_ORDER; row += 1) {
    for (let column = 0; column < WARP_ORDER; column += 1) {
      const point = source(row, column);
      next.push({ x: point.x, y: point.y });
    }
  }
  return next;
}

/**
 * Edit - Transform - Flip and Rotate. With a transform session open they change
 * the box, exactly as dragging a handle would; otherwise they are committed
 * straight away against the layer's own bounds.
 */
export function applyQuickTransform(kind: QuickTransform): void {
  if (session && context) {
    session.corners = cloneCorners(permuteCorners(session.corners, kind));
    if (session.mesh) {
      session.mesh = permuteMesh(session.mesh, kind);
      session.preset = 'custom';
    }
    applySession(context);
    return;
  }

  const store = useAppStore.getState();
  const doc = store.documents.find((item) => item.id === store.activeDocumentId);
  if (!doc) return;
  const layerId = movableLayer(doc);
  if (!layerId) {
    store.setStatus('The active layer is locked or not selected');
    return;
  }

  const canvas = { x: 0, y: 0, width: doc.width, height: doc.height };
  const bounds = selectionBounds() ?? layerContentBounds(layerId) ?? canvas;
  const matrix = mat3RectToQuad(bounds, permuteCorners(rectCorners(bounds), kind));
  if (transformShapeLayer(layerId, matrix, 'Transform Shape')) return;
  commitTransform(layerId, docMatrixToSrcUv(matrix, doc.width, doc.height));
}

/** Options bar and Warp need to know whether a session is open. */
export function activeTransformSession(): Session | null {
  return session;
}

/** Rewrites the box of the open session, for the numeric fields in the options bar. */
export function setTransformCorners(corners: Corners): void {
  if (!session || !context) return;
  session.corners = cloneCorners(corners);
  publishSession(context);
}

export function commitTransformSession(): void {
  if (session && context) finishSession(context);
}

export function cancelTransformSession(): void {
  if (context) transformTool.cancel?.(context);
  useAppStore.getState().setTool('move');
  context?.setStatus('Transform cancelled');
}

/** Bilinear point inside the box; exact whenever the box is a parallelogram. */
function anchorPoint(corners: Corners, anchor: TransformAnchor): Point {
  const { u, v } = TRANSFORM_ANCHORS.find((entry) => entry.id === anchor) ?? TRANSFORM_ANCHORS[4];
  const top = add(corners[0], scalePoint(subtract(corners[1], corners[0]), u));
  const bottom = add(corners[3], scalePoint(subtract(corners[2], corners[3]), u));
  return add(top, scalePoint(subtract(bottom, top), v));
}

/** What the numeric fields of the options bar show and write back. */
export interface TransformValues {
  /** Anchor position in document pixels. */
  x: number;
  y: number;
  /** Fractions of the original box, so 1 means 100%. */
  scaleX: number;
  scaleY: number;
  /** Degrees, clockwise on screen. */
  angle: number;
  /** Horizontal shear in degrees. */
  skew: number;
}

const DEG = 180 / Math.PI;

/**
 * Reads the box back as scale, rotation and shear. Only meaningful while the box
 * is a parallelogram, which is the case unless perspective has been dragged in;
 * that is also why Photoshop greys these fields out after a perspective drag.
 */
export function transformValues(anchor: TransformAnchor): TransformValues | null {
  if (!session) return null;
  const { corners, original } = session;
  const e1 = subtract(corners[1], corners[0]);
  const e2 = subtract(corners[3], corners[0]);
  const angle = Math.atan2(e1.y, e1.x);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  // e2 in the box's own frame: its x component is the shear, its y the height.
  const localY = { x: e2.x * cos + e2.y * sin, y: -e2.x * sin + e2.y * cos };
  const point = anchorPoint(corners, anchor);
  return {
    x: point.x,
    y: point.y,
    scaleX: Math.hypot(e1.x, e1.y) / (original.width || 1),
    scaleY: localY.y / (original.height || 1),
    angle: angle * DEG,
    skew: Math.abs(localY.y) < 1e-6 ? 0 : Math.atan(localY.x / localY.y) * DEG,
  };
}

/** Rebuilds the box from the numeric fields, holding the anchor point in place. */
export function applyTransformValues(
  anchor: TransformAnchor,
  patch: Partial<TransformValues>,
): void {
  if (!session || !context) return;
  const current = transformValues(anchor);
  if (!current) return;
  const values = { ...current, ...patch };

  const { original } = session;
  const radians = values.angle / DEG;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const rotate = (p: Point): Point => ({ x: p.x * cos - p.y * sin, y: p.x * sin + p.y * cos });
  const width = original.width * values.scaleX;
  const height = original.height * values.scaleY;
  const shear = Math.tan(values.skew / DEG) * height;
  const e1 = rotate({ x: width, y: 0 });
  const e2 = rotate({ x: shear, y: height });

  const cell = TRANSFORM_ANCHORS.find((entry) => entry.id === anchor) ?? TRANSFORM_ANCHORS[4];
  const offset = add(scalePoint(e1, cell.u), scalePoint(e2, cell.v));
  const origin = subtract({ x: values.x, y: values.y }, offset);
  setTransformCorners([
    origin,
    add(origin, e1),
    add(origin, add(e1, e2)),
    add(origin, e2),
  ]);
}

/** Rotation and symmetric scaling turn around the anchor the options bar picks. */
export function syncTransformPivot(anchor: TransformAnchor): void {
  if (!session) return;
  session.pivot = anchorPoint(session.corners, anchor);
}

/**
 * Switching in or out of Warp bakes whatever is on screen first, because a warp
 * and a matrix cannot be carried in the same session.
 */
export function setTransformMode(mode: TransformMode): void {
  const store = useAppStore.getState();
  const changesFamily = (store.toolOptions.transformMode === 'warp') !== (mode === 'warp');
  store.patchToolOptions({ transformMode: mode });
  if (!changesFamily || !session || !context) return;
  // Entering or leaving Warp swaps the mesh for a matrix, so the session has to be
  // rebuilt. The stored context still holds the options from the last pointer
  // event, and the restart reads the mode from it, so refresh it first.
  context = { ...context, options: useAppStore.getState().toolOptions };
  applySession(context);
}

export function warpSettings(): { preset: WarpPreset; bend: number } | null {
  return session?.mesh ? { preset: session.preset, bend: session.bend } : null;
}

export function setWarpPreset(preset: WarpPreset, bend: number): void {
  if (!session?.mesh || !context) return;
  session.preset = preset;
  session.bend = bend;
  session.mesh = presetMesh(preset, session.original, bend);
  publishSession(context);
}

/**
 * Move is a translation-only transform, committed on pointer up. With an area
 * selected it moves the selected pixels instead of the whole layer: they come
 * off as a floating selection and leave a hole behind, as in Photoshop.
 */
export const moveTool: Tool = (() => {
  let layerId: string | null = null;
  let startPointer: Point | null = null;
  let offset: Point = { x: 0, y: 0 };
  let float: FloatingSelection | null = null;

  const srcMatrixFor = (dx: number, dy: number, width: number, height: number): Mat3 =>
    new Float32Array([1, 0, 0, 0, 1, 0, -dx / width, -dy / height, 1]);

  function endDrag(): { target: string | null; moved: Point } {
    const target = layerId;
    const moved = offset;
    startPointer = null;
    layerId = null;
    liveRender.transform = null;
    if (target) setLivePreview('surface', target, null);
    return { target, moved };
  }

  return {
    id: 'move',
    cursor: 'move',
    hint: 'Drag the layer, or the selected pixels when an area is selected',

    onPointerDown(event, ctx) {
      layerId = movableLayer(ctx.doc);
      if (!layerId) {
        ctx.setStatus('The active layer is locked or not selected');
        return;
      }
      startPointer = event.point;
      offset = { x: 0, y: 0 };
      float = ctx.doc.hasSelection ? liftFloatingSelection(layerId, event.alt) : null;
      if (float) ctx.requestRender();
    },

    onPointerMove(event, ctx) {
      if (!startPointer || !layerId) return;
      offset = {
        x: Math.round(event.point.x - startPointer.x),
        y: Math.round(event.point.y - startPointer.y),
      };
      if (float) {
        setLivePreview('surface', layerId, float.preview(ctx.doc, layerId, offset));
      } else {
        liveRender.transform = {
          layerId,
          srcMatrix: srcMatrixFor(offset.x, offset.y, ctx.doc.width, ctx.doc.height),
        };
      }
      ctx.requestRender();
    },

    onPointerUp(_event, ctx) {
      const carried = float;
      float = null;
      const { target, moved } = endDrag();
      if (!target) {
        ctx.requestRender();
        return;
      }

      if (carried) {
        // A drag that went nowhere still has to put the lifted pixels back, but
        // it should not show up in the history.
        dropFloatingSelection(carried, target, moved, moved.x !== 0 || moved.y !== 0);
        ctx.requestRender();
        return;
      }
      if (moved.x === 0 && moved.y === 0) {
        ctx.requestRender();
        return;
      }
      // A shape moves as geometry, so it stays crisp instead of being resampled.
      if (transformShapeLayer(target, mat3Translation(moved.x, moved.y), 'Move Shape')) {
        ctx.requestRender();
        return;
      }
      if (moveTextLayer(target, moved.x, moved.y)) {
        ctx.requestRender();
        return;
      }
      nudgeLayerOffset(target, moved.x, moved.y);
      ctx.requestRender();
    },

    onDoubleClick(event, ctx) {
      const hit = topTextLayerAt(event.point);
      if (!hit) return;
      editTextLayer(hit.id);
      ctx.setStatus('Editing text — Ctrl+Enter to commit');
    },

    onKeyDown(event, ctx) {
      const step = event.shiftKey ? 10 : 1;
      const deltas: Record<string, Point> = {
        ArrowLeft: { x: -step, y: 0 },
        ArrowRight: { x: step, y: 0 },
        ArrowUp: { x: 0, y: -step },
        ArrowDown: { x: 0, y: step },
      };
      const delta = deltas[event.key];
      if (!delta) return false;
      const target = movableLayer(ctx.doc);
      if (!target) return false;
      if (ctx.doc.hasSelection) {
        const nudged = liftFloatingSelection(target, false);
        if (nudged) {
          dropFloatingSelection(nudged, target, delta, true);
          return true;
        }
      }
      if (moveTextLayer(target, delta.x, delta.y)) return true;
      return nudgeLayerOffset(target, delta.x, delta.y);
    },

    cancel(ctx) {
      const carried = float;
      float = null;
      const { target } = endDrag();
      if (carried && target) dropFloatingSelection(carried, target, { x: 0, y: 0 }, false);
      ctx.requestRender();
    },
  };
})();
